import { ANGLE_BUCKETS, ROTATION_CORPUS, itemsByAngle, } from './corpus';
import { angleDegFromRank, timing, variantForLevel, } from './params';
const FEEDBACK_MS = timing.feedbackMs;
const sessionMemory = { shown: new Set() };
export function resetSessionMemory() {
    sessionMemory.shown.clear();
}
/** Cherche un item au tier d'angle demandé, fallback aux tiers voisins. */
function pickItemForAngle(angleDeg) {
    const tiers = nearestAngleTiers(angleDeg);
    for (const t of tiers) {
        const pool = itemsByAngle(t).filter((i) => !sessionMemory.shown.has(i.id));
        if (pool.length > 0) {
            const choice = pool[Math.floor(Math.random() * pool.length)];
            sessionMemory.shown.add(choice.id);
            return choice;
        }
    }
    // Banque épuisée : reset puis reprend.
    sessionMemory.shown.clear();
    const all = itemsByAngle(angleDeg);
    if (all.length > 0) {
        const choice = all[Math.floor(Math.random() * all.length)];
        sessionMemory.shown.add(choice.id);
        return choice;
    }
    const fallback = ROTATION_CORPUS[Math.floor(Math.random() * ROTATION_CORPUS.length)];
    sessionMemory.shown.add(fallback.id);
    return fallback;
}
/** Retourne tiers d'angle proches dans l'ordre : exact, +/-1, +/-2 etc. */
function nearestAngleTiers(angleDeg) {
    const sorted = [...ANGLE_BUCKETS].sort((a, b) => Math.abs(a - angleDeg) - Math.abs(b - angleDeg));
    return sorted;
}
/** Snap angle (degrés) vers le tier le plus proche du corpus. */
function snapToBucket(angleDeg) {
    let best = ANGLE_BUCKETS[0];
    let bestDist = Math.abs(angleDeg - best);
    for (const t of ANGLE_BUCKETS) {
        const d = Math.abs(angleDeg - t);
        if (d < bestDist) {
            bestDist = d;
            best = t;
        }
    }
    return best;
}
export function makeStimulus(trialIdx, value, _level) {
    // value = item_rank (1..12). On dérive l'angle, on snap vers tier corpus.
    const rawAngle = angleDegFromRank(value);
    const angleDeg = snapToBucket(rawAngle);
    const item = pickItemForAngle(angleDeg);
    return {
        trialIdx,
        angleDeg: item.angleDeg,
        item,
    };
}
export function createThreeDRotationAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
        pickedPick: null,
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    return {
        mount() {
            // Idempotent — rendu déclaratif côté React.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({
                    subPhase: 'fixation',
                    stim,
                    feedback: null,
                    pickedPick: null,
                });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'stimulus' });
                    // Ouverture de la fenêtre réponse immédiatement après affichage.
                    // Pas de mask : les deux shapes restent visibles tant que la
                    // réponse n'a pas été donnée ou que le timeout n'est pas atteint.
                    update({ subPhase: 'awaiting_response' });
                    trialStart = Date.now();
                    timers.push(setTimeout(() => {
                        // Timeout = considéré comme erreur (pas de pick).
                        if (resolver) {
                            const rt = Date.now() - trialStart;
                            const resp = {
                                pick: 'identical',
                                isIdentical: stim.item.isIdentical,
                                correct: false,
                                reactionTimeMs: rt,
                            };
                            const r = resolver;
                            resolver = null;
                            rejecter = null;
                            r(resp);
                        }
                    }, state.variant.responseTimeoutMs));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, FEEDBACK_MS));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handlePick(pick) {
            if (state.subPhase !== 'awaiting_response')
                return;
            const stim = state.stim;
            if (!stim)
                return;
            const correct = (pick === 'identical') === stim.item.isIdentical;
            const rt = Date.now() - trialStart;
            const resp = {
                pick,
                isIdentical: stim.item.isIdentical,
                correct,
                reactionTimeMs: rt,
            };
            update({ pickedPick: pick });
            if (resolver) {
                const r = resolver;
                resolver = null;
                rejecter = null;
                r(resp);
            }
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
        resetSession() {
            resetSessionMemory();
        },
    };
}
//# sourceMappingURL=adapter.js.map